import { useRef, useState, type FormEvent } from "react";
import {
  CanAccess,
  useDataProvider,
  useGetOne,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";
import { Navigate, useNavigate, useParams } from "react-router";
import { Bold, Italic, Link as LinkIcon, List as ListIcon } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";

import { ListErrorState, ListRowsSkeleton } from "../design/ListStates";
import { MobileContent } from "../layout/MobileContent";
import MobileHeader from "../layout/MobileHeader";
import { Markdown } from "../misc/Markdown";
import { MobileBackButton } from "../misc/MobileBackButton";
import type { CultureDocument, CultureDocumentCurrent } from "../types";
import {
  applyBoldFormatting,
  applyBulletFormatting,
  applyItalicFormatting,
  applyLinkFormatting,
  hasCultureDocumentChanges,
  slugifyCultureTitle,
  validateCultureBody,
  validateCultureTitle,
  type TextSelection,
} from "./cultureDocumentForm";

const CULTURE_PAGE_PATH = "/almikia/cultura";

type FormattingAction = "bold" | "italic" | "bullet" | "link";

/** Dispatches to the pure formatting-help function for `action`. */
const applyFormatting = (
  action: FormattingAction,
  selection: TextSelection,
): TextSelection => {
  switch (action) {
    case "bold":
      return applyBoldFormatting(selection);
    case "italic":
      return applyItalicFormatting(selection);
    case "bullet":
      return applyBulletFormatting(selection);
    case "link":
      return applyLinkFormatting(selection);
  }
};

/**
 * "Guardar nueva versión" -- for an existing document (`documentId` set)
 * this is a single `create` on `culture_document_versions` (never an
 * `update`, DOMAIN RULES EO (e)); for "Nuevo documento" (`documentId`
 * undefined) it first creates the `culture_documents` row (its slug/
 * sort_order computed here, never client-guessed against stale data) then
 * its version 1. `version`/`created_by` are never sent -- the data layer
 * (TASK-097, `orgChartProvider.ts`) server-assigns both and rejects a
 * client-supplied value.
 *
 * `documentId` MUST be the numeric/opaque id fetched from the
 * `culture_documents_current` record (`currentDocument.id`), never the raw
 * string route param -- FakeRest's `computeCultureDocumentsCurrent` looks up
 * versions by the record's real id, so sending the route param string would
 * silently mismatch it in demo mode (the page and editor would keep showing
 * the OLD text after a save).
 */
const CultureDocumentEditorForm = ({
  documentId,
  initialTitle,
  initialBody,
}: {
  documentId?: Identifier;
  initialTitle: string;
  initialBody: string;
}) => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [title, setTitle] = useState(initialTitle);
  const [body, setBody] = useState(initialBody);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const isNew = documentId == null;

  const handleFormat = (action: FormattingAction) => {
    const textarea = textareaRef.current;
    const selection: TextSelection = {
      value: body,
      selectionStart: textarea?.selectionStart ?? body.length,
      selectionEnd: textarea?.selectionEnd ?? body.length,
    };
    const result = applyFormatting(action, selection);
    setBody(result.value);
    // The textarea only re-renders with the new value on the next tick;
    // restoring the selection now would apply it to the STALE DOM value.
    requestAnimationFrame(() => {
      textarea?.focus();
      textarea?.setSelectionRange(result.selectionStart, result.selectionEnd);
    });
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);

    const titleError = validateCultureTitle(title);
    if (titleError) {
      setFormError(translate(titleError));
      return;
    }
    const bodyError = validateCultureBody(body);
    if (bodyError) {
      setFormError(translate(bodyError));
      return;
    }
    if (
      !isNew &&
      !hasCultureDocumentChanges(
        { title: initialTitle, body_markdown: initialBody },
        { title, body_markdown: body },
      )
    ) {
      setFormError(translate("crm.culture.editor.error.no_changes"));
      return;
    }

    setIsSubmitting(true);
    try {
      let targetDocumentId: Identifier = documentId ?? "";
      if (isNew) {
        const { data: existingDocuments } =
          await dataProvider.getList<CultureDocument>("culture_documents", {
            filter: {},
            pagination: { page: 1, perPage: 100 },
            sort: { field: "sort_order", order: "DESC" },
          });
        const nextSortOrder = (existingDocuments[0]?.sort_order ?? 0) + 1;
        const slug = slugifyCultureTitle(
          title,
          existingDocuments.map((document) => document.slug),
        );
        const { data: createdDocument } =
          await dataProvider.create<CultureDocument>("culture_documents", {
            data: { slug, sort_order: nextSortOrder },
          });
        targetDocumentId = createdDocument.id;
      }

      await dataProvider.create("culture_document_versions", {
        data: {
          document_id: targetDocumentId,
          title: title.trim(),
          body_markdown: body.trim(),
        },
      });

      notify("crm.culture.editor.success", { type: "success" });
      navigate(CULTURE_PAGE_PATH);
    } catch (error) {
      // A rejected save (e.g. an RLS error) keeps the typed content in the
      // form -- neither `title`/`body` state is cleared here. The raw error
      // (a Postgres/RLS message on Supabase) is never shown to the user --
      // only the translated, friendly Spanish message is, per the
      // "friendly Spanish error" acceptance criterion.
      console.error("Failed to save culture document version:", error);
      setFormError(translate("crm.culture.editor.error.save_failed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="culture-document-title">
          {translate("crm.culture.editor.title_label")}
        </Label>
        <Input
          id="culture-document-title"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          disabled={isSubmitting}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="culture-document-body">
          {translate("crm.culture.editor.body_label")}
        </Label>
        <div
          role="toolbar"
          aria-label={translate("crm.culture.editor.toolbar_label")}
          className="flex flex-wrap gap-1"
        >
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="size-11"
            onClick={() => handleFormat("bold")}
            disabled={isSubmitting}
          >
            <Bold className="size-4" />
            <span className="sr-only">
              {translate("crm.culture.editor.format.bold")}
            </span>
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="size-11"
            onClick={() => handleFormat("italic")}
            disabled={isSubmitting}
          >
            <Italic className="size-4" />
            <span className="sr-only">
              {translate("crm.culture.editor.format.italic")}
            </span>
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="size-11"
            onClick={() => handleFormat("bullet")}
            disabled={isSubmitting}
          >
            <ListIcon className="size-4" />
            <span className="sr-only">
              {translate("crm.culture.editor.format.bullet")}
            </span>
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="size-11"
            onClick={() => handleFormat("link")}
            disabled={isSubmitting}
          >
            <LinkIcon className="size-4" />
            <span className="sr-only">
              {translate("crm.culture.editor.format.link")}
            </span>
          </Button>
        </div>

        <Tabs defaultValue="edit">
          <TabsList>
            <TabsTrigger value="edit" className="min-h-11">
              {translate("crm.culture.editor.tab.edit")}
            </TabsTrigger>
            <TabsTrigger value="preview" className="min-h-11">
              {translate("crm.culture.editor.tab.preview")}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="edit">
            <Textarea
              id="culture-document-body"
              ref={textareaRef}
              value={body}
              onChange={(event) => setBody(event.target.value)}
              rows={12}
              className="text-base md:text-sm"
              disabled={isSubmitting}
            />
          </TabsContent>
          <TabsContent value="preview">
            <div className="rounded-md border border-border p-3">
              {body.trim() ? (
                <Markdown className="text-base">{body}</Markdown>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {translate("crm.culture.editor.preview_empty")}
                </p>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {formError ? (
        <p role="alert" className="text-sm text-destructive">
          {formError}
        </p>
      ) : null}

      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          className="min-h-11"
          onClick={() => navigate(CULTURE_PAGE_PATH)}
          disabled={isSubmitting}
        >
          {translate("ra.action.cancel")}
        </Button>
        <Button type="submit" className="min-h-11" disabled={isSubmitting}>
          {translate("crm.culture.editor.action.save")}
        </Button>
      </div>
    </form>
  );
};

const CultureDocumentEditorContent = () => {
  const { documentId } = useParams<{ documentId?: string }>();
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const isNew = documentId == null;

  const {
    data: currentDocument,
    isPending,
    error,
  } = useGetOne<CultureDocumentCurrent>(
    "culture_documents_current",
    { id: documentId as string },
    { enabled: !isNew },
  );

  const heading = isNew
    ? translate("crm.culture.editor.new_title")
    : translate("crm.culture.editor.edit_title", {
        title: currentDocument?.title ?? "",
      });

  const body = isNew ? (
    <CultureDocumentEditorForm initialTitle="" initialBody="" />
  ) : isPending ? (
    <ListRowsSkeleton rows={4} columns={1} />
  ) : error || !currentDocument ? (
    <ListErrorState message={translate("crm.common.list_error")} />
  ) : (
    <CultureDocumentEditorForm
      documentId={currentDocument.id}
      initialTitle={currentDocument.title}
      initialBody={currentDocument.body_markdown}
    />
  );

  if (isMobile) {
    return (
      <>
        <MobileHeader>
          <MobileBackButton to={CULTURE_PAGE_PATH} />
          <div className="flex flex-1 min-w-0">
            <h1 className="text-xl font-semibold truncate">{heading}</h1>
          </div>
        </MobileHeader>
        <MobileContent>{body}</MobileContent>
      </>
    );
  }

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-4 p-4">
      <h1 className="text-2xl font-bold">{heading}</h1>
      {body}
    </div>
  );
};

/**
 * Almikia Epic EO (TASK-100): the direccion-only culture editor -- reached
 * either as "Editar" on an existing document (`:documentId/editar`) or as
 * "Nuevo documento" (`/nuevo`, no `documentId`). Gated the same way as
 * `IncentiveProgramEdit`/`GoalsPage`: `CanAccess` is defense in depth on top
 * of the RLS/FakeRest write gate (TASK-096/TASK-097) -- a non-direccion role
 * hitting either route directly is redirected to the culture page and NEVER
 * sees the form, whatever it reads from `canAccess.ts` client-side.
 */
export const CultureDocumentEditor = () => (
  <CanAccess
    resource="culture_documents"
    action="create"
    accessDenied={<Navigate to={CULTURE_PAGE_PATH} replace />}
  >
    <CultureDocumentEditorContent />
  </CanAccess>
);

CultureDocumentEditor.path = "/almikia/cultura/:documentId/editar";
CultureDocumentEditor.newPath = "/almikia/cultura/nuevo";
